import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { CharacterTier, NoChatPromptRule, NoChatScenario } from "../types";
import { EmailBodyEditor } from "./EmailBodyEditor";
import { getCharacterById } from "../../../../api/rest-apis";
import { groupByForPlan } from "../../../../helpers/utm";

interface CharacterOption {
  id: string;
  name: string;
  tier: CharacterTier;
}

interface NoChatScenariosEditorProps {
  scenarios: NoChatScenario[];
  characterOptions: CharacterOption[];
  onChange: (scenarios: NoChatScenario[]) => void;
}

const generateId = (prefix: string) =>
  `${prefix}-${Math.random().toString(36).slice(2, 8)}-${Date.now()}`;

interface PromptSection {
  title: string;
  prompts: string[];
}

interface CharacterPromptLibrary {
  assistantSections: PromptSection[];
  bookTalkSections: PromptSection[];
}

const PLAN_LABEL_MAP: Record<string, string> = {
  basic: "subscriber",
  plus: "patron",
};

const PLAN_ORDER = ["subscriber", "patron", "premium"];

const buildSections = (
  assistants: any[] = [],
  labelPrefix: string
): PromptSection[] => {
  if (!assistants.length) return [];
  const grouped = groupByForPlan(assistants);

  const entries = Object.entries(grouped)
    .map(([planKey, items]) => {
      const displayName = PLAN_LABEL_MAP[planKey] ?? planKey;
      const prompts = items
        .flatMap((item) => item.sample_prompts ?? [])
        .filter(
          (prompt: unknown): prompt is string =>
            typeof prompt === "string" && prompt.trim().length > 0
        );
      return {
        planKey,
        displayName,
        prompts,
      };
    })
    .filter((entry) => entry.prompts.length > 0);

  entries.sort((a, b) => {
    const indexA = PLAN_ORDER.indexOf(a.displayName);
    const indexB = PLAN_ORDER.indexOf(b.displayName);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return a.displayName.localeCompare(b.displayName);
  });

  return entries.map((entry) => ({
    title: `${labelPrefix} – ${entry.displayName}`,
    prompts: entry.prompts,
  }));
};

const buildCharacterPromptLibrary = (
  character: any
): CharacterPromptLibrary => ({
  assistantSections: buildSections(character?.assistants ?? [], "Assistant"),
  bookTalkSections: buildSections(
    character?.book_talk_assistants ?? [],
    "Book Talk"
  ),
});

export const NoChatScenariosEditor: React.FC<NoChatScenariosEditorProps> = ({
  scenarios,
  characterOptions,
  onChange,
}) => {
  const [activeId, setActiveId] = useState<string>(scenarios[0]?.id ?? "");
  const [promptCache, setPromptCache] = useState<
    Record<string, CharacterPromptLibrary>
  >({});
  const [loadingCharacters, setLoadingCharacters] = useState<
    Record<string, boolean>
  >({});
  const [promptErrors, setPromptErrors] = useState<Record<string, string>>({});

  const activeScenario = useMemo(
    () => scenarios.find((scenario) => scenario.id === activeId),
    [scenarios, activeId]
  );

  useEffect(() => {
    if (!scenarios.length) {
      setActiveId("");
      return;
    }
    if (!scenarios.some((scenario) => scenario.id === activeId)) {
      setActiveId(scenarios[0].id);
    }
  }, [scenarios, activeId]);

  const ensureCharacterPrompts = useCallback(
    async (characterId: string) => {
      if (
        !characterId ||
        promptCache[characterId] ||
        loadingCharacters[characterId]
      ) {
        return;
      }
      setLoadingCharacters((current) => ({ ...current, [characterId]: true }));
      try {
        const response: any = await getCharacterById(characterId);
        const character = response?.characters?.[0];
        if (!character) {
          throw new Error("Character not found.");
        }
        const library = buildCharacterPromptLibrary(character);
        setPromptCache((current) => ({ ...current, [characterId]: library }));
        setPromptErrors((current) => {
          const next = { ...current };
          delete next[characterId];
          return next;
        });
      } catch (error) {
        console.error("Failed to load character prompts", error);
        setPromptErrors((current) => ({
          ...current,
          [characterId]:
            error instanceof Error
              ? error.message
              : "Failed to load prompts for this character.",
        }));
      } finally {
        setLoadingCharacters((current) => ({
          ...current,
          [characterId]: false,
        }));
      }
    },
    [promptCache, loadingCharacters]
  );

  useEffect(() => {
    scenarios.forEach((scenario) => {
      if (scenario.characterId) {
        void ensureCharacterPrompts(scenario.characterId);
      }
    });
  }, [scenarios, ensureCharacterPrompts]);

  const persist = (next: NoChatScenario[]) => {
    onChange(next);
  };

  const updateScenario = (
    scenarioId: string,
    updates: Partial<Omit<NoChatScenario, "id">>
  ) => {
    const next = scenarios.map((scenario) =>
      scenario.id === scenarioId
        ? { ...scenario, ...updates, lastUpdated: new Date().toISOString() }
        : scenario
    );
    persist(next);
  };

  const addScenario = () => {
    const defaultCharacterId = characterOptions[0]?.id ?? "";
    const newScenario: NoChatScenario = {
      id: generateId("scenario"),
      characterId: defaultCharacterId,
      from_character_id: defaultCharacterId,
      storylineTitle: "New storyline trigger",
      prompts: [],
      subject: "Subject line",
      manualEmailSubject: "Subject line",
      body: "Draft the intro email for this storyline.",
      manualEmailBody: "Draft the intro email for this storyline.",
      // Start in prompt mode (manual disabled) to match reach-out behavior
      manualEmailEnabled: false,
      lastUpdated: new Date().toISOString(),
    };
    const next = [...scenarios, newScenario];
    persist(next);
    setActiveId(newScenario.id);
    void ensureCharacterPrompts(defaultCharacterId);
  };

  const removeScenario = (scenarioId: string) => {
    const next = scenarios.filter((scenario) => scenario.id !== scenarioId);
    persist(next);
    if (activeId === scenarioId) {
      setActiveId(next[0]?.id ?? "");
    }
  };

  const normalizePromptLabel = (text?: string) =>
    text?.trim().toLowerCase().replace(/\s+/g, " ") ?? "";

  const promptsMatch = (
    existing: NoChatPromptRule,
    candidate: NoChatPromptRule
  ) => {
    if (existing.id && candidate.id && existing.id === candidate.id) {
      return true;
    }
    return (
      normalizePromptLabel(existing.label) ===
      normalizePromptLabel(candidate.label)
    );
  };

  const togglePromptSelection = (
    scenario: NoChatScenario,
    promptRule: NoChatPromptRule
  ) => {
    const isSelected = scenario.prompts.some((prompt) =>
      promptsMatch(prompt, promptRule)
    );
    const nextPrompts = isSelected
      ? scenario.prompts.filter((prompt) => !promptsMatch(prompt, promptRule))
      : [
          ...scenario.prompts,
          { ...promptRule, label: promptRule.label.trim() },
        ];
    updateScenario(scenario.id, { prompts: nextPrompts });
  };

  const getContentMode = (scenario: NoChatScenario): "prompt" | "manual" => {
    if (scenario.manualEmailEnabled === false) return "prompt";
    if (scenario.manualEmailEnabled === true) return "manual";
    return scenario.promptInstructions ? "prompt" : "manual";
  };

  const setContentMode = (scenarioId: string, mode: "prompt" | "manual") => {
    updateScenario(scenarioId, {
      manualEmailEnabled: mode === "manual",
      // Clear prompt instructions when opting into manual to avoid stale data confusion
      ...(mode === "manual" ? { promptInstructions: "" } : {}),
    });
  };

  const resolveManualEmailSubject = (scenario: NoChatScenario) =>
    scenario.manualEmailSubject ?? scenario.subject ?? "";

  const resolveManualEmailBody = (scenario: NoChatScenario) =>
    scenario.manualEmailBody ?? scenario.body ?? "";

  const getCharacterName = (characterId: string) =>
    characterOptions.find((option) => option.id === characterId)?.name ||
    "Unassigned";

  const createPromptRule = (
    source: "assistant" | "bookTalk",
    promptText: string,
    sectionTitle: string
  ): NoChatPromptRule => ({
    id: `${source}:${sectionTitle}:${promptText}`,
    label: promptText.trim(),
    preview: sectionTitle,
    enabled: true,
  });

  const renderPromptColumn = (
    scenario: NoChatScenario,
    sections: PromptSection[],
    source: "assistant" | "bookTalk"
  ) => {
    if (!sections.length) {
      return (
        <div className="rounded-2xl border border-dashed border-gray-200 px-4 py-6 text-sm text-gray-500">
          No {source === "assistant" ? "assistant" : "book talk"} prompts found
          for this character.
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {sections.map((section) => (
          <div key={`${source}-${section.title}`}>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {section.title}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {section.prompts.map((promptText) => {
                const promptRule = createPromptRule(
                  source,
                  promptText,
                  section.title
                );
                const isSelected = scenario.prompts.some((prompt) =>
                  promptsMatch(prompt, promptRule)
                );
                return (
                  <button
                    key={promptRule.id}
                    type="button"
                    onClick={() => togglePromptSelection(scenario, promptRule)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                      isSelected
                        ? "border-[var(--primary-color)] bg-[var(--primary-color)]/10 text-[var(--primary-color)]"
                        : "border-gray-200 text-gray-600 hover:border-[var(--primary-color)]/50 hover:text-[var(--primary-color)]"
                    }`}
                  >
                    {promptText}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
            Trigger scenarios
          </span>
          <div className="text-xs text-gray-500">
            Pair a character with specific prompt clicks to decide when the
            intro fires.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-xl bg-[var(--primary-color)] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:shadow"
            onClick={addScenario}
          >
            Add scenario
          </button>
          {activeScenario ? (
            <button
              type="button"
              className="rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-500 transition hover:bg-red-50"
              onClick={() => removeScenario(activeScenario.id)}
            >
              Remove
            </button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px,1fr]">
        <div className="space-y-2">
          {scenarios.map((scenario) => {
            const enabledCount = scenario.prompts.length;
            return (
              <button
                key={scenario.id}
                type="button"
                onClick={() => setActiveId(scenario.id)}
                className={`w-full rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition ${
                  activeId === scenario.id
                    ? "border-[var(--primary-color)] bg-[var(--primary-color)]/10 text-[var(--primary-color)]"
                    : "border-gray-200 text-gray-700 hover:border-[var(--primary-color)] hover:bg-[var(--primary-color)]/10"
                }`}
              >
                <div>{scenario.storylineTitle}</div>
                <div className="text-xs font-medium text-gray-500">
                  {getCharacterName(scenario.characterId)} · {enabledCount}{" "}
                  {enabledCount === 1 ? "prompt" : "prompts"} selected
                </div>
              </button>
            );
          })}
          {scenarios.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 px-4 py-6 text-center text-xs text-gray-500">
              Add a trigger scenario to connect prompt clicks with the intro
              email.
            </div>
          ) : null}
        </div>

        {activeScenario ? (
          <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="flex flex-col gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-600">
                  Storyline or scenario name
                </span>
                <input
                  type="text"
                  value={activeScenario.storylineTitle}
                  onChange={(event) =>
                    updateScenario(activeScenario.id, {
                      storylineTitle: event.target.value,
                    })
                  }
                  className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                />
              </label>

              <label className="flex flex-col gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-600">
                  Character
                </span>
                <div className="relative">
                  <select
                    value={activeScenario.characterId}
                    onChange={(event) => {
                      const nextCharacterId = event.target.value;
                      updateScenario(activeScenario.id, {
                        characterId: nextCharacterId,
                      });
                      void ensureCharacterPrompts(nextCharacterId);
                    }}
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 pr-32 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                  >
                    {characterOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>
              </label>

              <label className="flex flex-col gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-600">
                  Send email from
                </span>
                <select
                  value={activeScenario.from_character_id ?? activeScenario.characterId}
                  onChange={(event) =>
                    updateScenario(activeScenario.id, {
                      from_character_id: event.target.value,
                    })
                  }
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                >
                  {characterOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-gray-500">
                  Choose which character the no-chat intro email comes from.
                </span>
              </label>

              <label className="flex flex-col gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-600">
                  Trigger if user has ≤ conversations
                </span>
                <input
                  type="number"
                  min={0}
                  placeholder="Any"
                  value={
                    typeof activeScenario.maxConversationCount === "number"
                      ? activeScenario.maxConversationCount
                      : 0
                  }
                  onChange={(event) => {
                    const nextValue = event.target.value.trim();
                    updateScenario(activeScenario.id, {
                      maxConversationCount:
                        nextValue === "" ? null : Number(event.target.value),
                    });
                  }}
                  className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                />
                <span className="text-xs text-gray-500">
                  Optional guardrail to only send if they chatted at most this
                  many times.
                </span>
              </label>
            </div>

            <div className="space-y-3 rounded-2xl border border-gray-100 bg-gray-50 p-4">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Pick the prompts that, if clicked without starting a chat,
                  should trigger this intro email
                </span>
                <div className="text-xs text-gray-500">
                  Use this to capture readers who sampled a story, never spent
                  tokens, and haven&apos;t chatted with a character yet.
                </div>
              </div>

              {loadingCharacters[activeScenario.characterId ?? ""] ? (
                <div className="rounded-2xl border border-dashed border-gray-200 px-4 py-6 text-sm text-gray-500">
                  Loading prompt library for this character...
                </div>
              ) : promptErrors[activeScenario.characterId ?? ""] ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700">
                  {promptErrors[activeScenario.characterId ?? ""]}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {renderPromptColumn(
                    activeScenario,
                    promptCache[activeScenario.characterId ?? ""]
                      ?.assistantSections ?? [],
                    "assistant"
                  )}
                  {renderPromptColumn(
                    activeScenario,
                    promptCache[activeScenario.characterId ?? ""]
                      ?.bookTalkSections ?? [],
                    "bookTalk"
                  )}
                </div>
              )}

              {!activeScenario.prompts.length ? (
                <div className="rounded-2xl border border-dashed border-gray-300 px-4 py-6 text-center text-xs text-gray-500">
                  Select at least one prompt to define this trigger.
                </div>
              ) : null}
            </div>

            <div className="space-y-3 rounded-2xl border border-gray-100 bg-gray-50 p-4">
              <div className="inline-flex rounded-full border border-gray-200 bg-white/70 p-1 text-xs font-semibold text-gray-600">
                {(["prompt", "manual"] as const).map((modeOption) => (
                  <button
                    key={modeOption}
                    type="button"
                    onClick={() => setContentMode(activeScenario.id, modeOption)}
                    className={`rounded-full px-4 py-1.5 transition ${
                      getContentMode(activeScenario) === modeOption
                        ? "bg-white text-[var(--primary-color)] shadow"
                        : "text-gray-500"
                    }`}
                  >
                    {modeOption === "prompt"
                      ? "Prompt instructions"
                      : "Manual email"}
                  </button>
                ))}
              </div>

              {getContentMode(activeScenario) === "prompt" ? (
                <label className="flex flex-col gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-600">
                    Prompt instructions
                  </span>
                  <textarea
                    value={activeScenario.promptInstructions ?? ""}
                    onChange={(event) =>
                      updateScenario(activeScenario.id, {
                        promptInstructions: event.target.value,
                      })
                    }
                    rows={5}
                    placeholder="Outline how this character should follow up when this rule is matched."
                    className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                  />
                  <span className="text-xs text-gray-500">
                    Guide how this character should follow up when this trigger fires. Switch to manual email if you want to send a prewritten message instead.
                  </span>
                  <span className="text-xs text-gray-500">
                    You can reference {"{{chatHistory}}"} to pull in the user's past conversation with this character.
                  </span>
                </label>
              ) : (
                <>
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-600">
                      Email subject
                    </span>
                    <input
                      type="text"
                      value={resolveManualEmailSubject(activeScenario)}
                      onChange={(event) =>
                        updateScenario(activeScenario.id, {
                          subject: event.target.value,
                          manualEmailSubject: event.target.value,
                        })
                      }
                      className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                      placeholder="Example: Cage kept the next chapter warm for you"
                    />
                  </label>

                  <EmailBodyEditor
                    label="Email body"
                    value={resolveManualEmailBody(activeScenario)}
                    onChange={(next) =>
                      updateScenario(activeScenario.id, {
                        body: next,
                        manualEmailBody: next,
                      })
                    }
                    helperText="Use merge tags like {{firstName}}."
                    minRows={8}
                  />
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center rounded-2xl border border-dashed border-gray-300 py-10 text-sm text-gray-500">
            Select a scenario to define its watched prompts.
          </div>
        )}
      </div>
    </div>
  );
};
